import { useEffect, useState } from 'react';
import type { Platform } from '../types';

function readUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    startapp: params.get('startapp') || params.get('tgWebAppStartParam') || undefined,
    event: params.get('event') || params.get('code') || undefined,
    mode: params.get('mode') || undefined,
  };
}

function parseStartParam(raw?: string) {
  if (!raw) return undefined;
  const decoded = decodeURIComponent(raw);
  if (decoded === 'admin') return 'admin';
  if (decoded.startsWith('evt_')) return decoded;
  return `evt_${decoded}`;
}

function parseStartParamFromInitData(initData: string): string | undefined {
  if (!initData.trim()) return undefined;
  try {
    return new URLSearchParams(initData).get('start_param') || undefined;
  } catch {
    return undefined;
  }
}

function resolveStartParam(initData: string): string | undefined {
  const url = readUrlParams();
  const tg = window.Telegram?.WebApp;
  const fromUnsafe = tg?.initDataUnsafe?.start_param;
  const fromMax = window.WebApp?.initDataUnsafe?.start_param;
  const fromInitData = parseStartParamFromInitData(initData);
  const fromUrl = url.startapp || url.event;

  return parseStartParam(
    fromUnsafe ||
      fromMax ||
      fromInitData ||
      fromUrl ||
      (url.mode === 'admin' ? 'admin' : undefined)
  );
}

function readMaxInitData(): string {
  const max = window.WebApp;
  if (max?.initData?.trim()) return max.initData.trim();

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return '';

  const params = new URLSearchParams(hash);
  return params.get('WebAppData')?.trim() || '';
}

function isMaxEnvironment(): boolean {
  if (window.location.hash.includes('WebAppData=')) return true;
  return Boolean(window.WebApp?.initData?.trim());
}

function shouldUseLocalDevMode(): boolean {
  if (!import.meta.env.DEV) return false;
  if (readMaxInitData()) return false;
  if (readTelegramInitData()) return false;
  const hash = window.location.hash;
  return !hash.includes('WebAppData=') && !hash.includes('tgWebAppData=');
}

function isTelegramEnvironment(): boolean {
  if (isMaxEnvironment()) return false;
  if (window.location.hash.includes('WebAppData=')) return false;
  return (
    Boolean(window.Telegram?.WebApp?.initData?.trim()) ||
    window.location.hash.includes('tgWebAppData=')
  );
}

function loadTelegramBridge(): Promise<void> {
  if (window.Telegram?.WebApp) return Promise.resolve();

  return new Promise((resolve) => {
    const existing = document.querySelector('script[data-telegram-bridge="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://telegram.org/js/telegram-web-app.js';
    script.async = true;
    script.dataset.telegramBridge = 'true';
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });
}

function readTelegramInitData(): string {
  const tg = window.Telegram?.WebApp;
  if (tg?.initData?.trim()) return tg.initData;

  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return '';

  const params = new URLSearchParams(hash);
  return params.get('tgWebAppData')?.trim() || '';
}

function eventCodeFromStartParam(startParam?: string): string | null {
  if (!startParam || startParam === 'admin') return null;
  if (startParam.startsWith('evt_')) return startParam.slice(4);
  return startParam;
}

export function useWebApp() {
  const [initData, setInitData] = useState('');
  const [platform, setPlatform] = useState<Platform>('dev');
  const [startParam, setStartParam] = useState<string | undefined>();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;

    function finish(data: string, plat: Platform) {
      if (cancelled) return;
      setInitData(data);
      setPlatform(plat);
      setStartParam(resolveStartParam(data));
      setReady(true);
    }

    function init(attempt = 0) {
      if (cancelled) return;

      const maxData = readMaxInitData();
      if (maxData) {
        try {
          window.WebApp?.ready?.();
          window.WebApp?.expand?.();
        } catch {
          // ignore
        }
        finish(maxData, 'max');
        return;
      }

      if (isMaxEnvironment() || window.WebApp?.initData?.trim()) {
        if (attempt < 100) {
          window.setTimeout(() => init(attempt + 1), 100);
          return;
        }
        if (import.meta.env.DEV) {
          finish('dev_mode', 'dev');
        } else {
          finish('', 'max');
        }
        return;
      }

      if (shouldUseLocalDevMode() && attempt >= 3) {
        finish('dev_mode', 'dev');
        return;
      }

      if (attempt === 0 && import.meta.env.DEV) {
        void loadTelegramBridge();
      }

      if (isTelegramEnvironment()) {
        const tg = window.Telegram?.WebApp;
        try {
          tg?.ready();
          tg?.expand();
        } catch {
          // ignore
        }

        const data = readTelegramInitData();
        const start = resolveStartParam(data);

        if (data && start) {
          finish(data, 'telegram');
          return;
        }

        if (data && attempt >= 30) {
          finish(data, 'telegram');
          return;
        }

        if (attempt < 50) {
          window.setTimeout(() => init(attempt + 1), 100);
          return;
        }

        if (import.meta.env.DEV) {
          finish('dev_mode', 'dev');
        } else {
          finish('', 'telegram');
        }
        return;
      }

      if (attempt < (import.meta.env.DEV ? 5 : 40)) {
        window.setTimeout(() => init(attempt + 1), 100);
        return;
      }

      if (import.meta.env.DEV) {
        finish('dev_mode', 'dev');
      } else {
        finish('', 'max');
      }
    }

    init();
    return () => {
      cancelled = true;
    };
  }, []);

  // start_param иногда появляется позже initData (ссылки ?startapp=)
  useEffect(() => {
    if (!ready || platform !== 'telegram') return;

    let cancelled = false;
    let attempt = 0;

    const poll = () => {
      if (cancelled) return;
      const next = resolveStartParam(initData);
      if (next) {
        setStartParam(next);
        return;
      }
      if (attempt++ < 30) {
        window.setTimeout(poll, 100);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [ready, initData, platform]);

  // start_param в MAX иногда появляется чуть позже initData
  useEffect(() => {
    if (!ready || platform !== 'max') return;

    let cancelled = false;
    let attempt = 0;

    const poll = () => {
      if (cancelled) return;
      const next = resolveStartParam(initData);
      if (next) {
        setStartParam(next);
        return;
      }
      if (attempt++ < 30) {
        window.setTimeout(poll, 100);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [ready, initData, platform]);

  const url = readUrlParams();
  const isAdminMode = startParam === 'admin' || url.mode === 'admin';
  const eventCode = eventCodeFromStartParam(startParam);

  return {
    ready,
    initData,
    platform,
    startParam,
    eventCode,
    isAdminMode,
    isDev: platform === 'dev',
  };
}

declare global {
  interface Window {
    Telegram?: {
      WebApp?: {
        initData: string;
        initDataUnsafe?: { start_param?: string; user?: { id: number; first_name?: string; last_name?: string } };
        ready: () => void;
        expand: () => void;
        close: () => void;
      };
    };
  }
}
